// @flow

import React from 'react';
import type { Node } from 'react';
import styled from 'styled-components';
import { flex, Width, Color, p12, topMargin, topPadding, bottomPadding } from 'styles';
import type { AllImages } from 'hooks/use-images';
import { ImageLoader } from '../../Gui';
import { useImages } from '../../../hooks';

const BREAK_DESKTOP_LAYOUT = 1000;

const Wrapper = styled.section`
  ${flex()};
  flex-direction: column;
  ${topPadding(82, 80, 100)};
  ${bottomPadding(20, 40, 50)};

  @media print, screen and (min-width: ${BREAK_DESKTOP_LAYOUT}px) {
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }
`;

const Image = styled(ImageLoader)``;

const Text = styled.div`
  width: 100%;
  text-align: center;

  @media print, screen and (min-width: ${BREAK_DESKTOP_LAYOUT}px) {
    text-align: left;
  }

  h1 {
    max-width: 305px;
    margin: 20px auto;
    font-size: 4.2rem;
    letter-spacing: calc(-0.5em / 28);
    color: ${Color.WHITE};

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      max-width: unset;
      font-size: 5.4rem;
    }

    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      margin: 0 auto 30px auto;
    }
  }

  p {
    max-width: 300px;
    margin: 0 auto;
    color: ${Color.WHITE};

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      max-width: unset;
    }
  }
`;

const ProductText = styled.div`
  margin-bottom: 48px;
  h3,
  p {
    color: ${Color.WHITE};
    margin-bottom: 10px;
  }
  h3 {
    font-size: 2rem;
  }
  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    flex: 1 0 auto;
    ${flex()};
    flex-direction: column;
    align-items: flex-start;
    max-width: 50%;
  }
  @media print, screen and (min-width: ${BREAK_DESKTOP_LAYOUT}px) {
    margin-bottom: 32px;
  }
  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    flex: unset;
    max-width: 65%;
  }
`;

const BREAK_ROW = 1100;

const Buttons = styled.div`
  margin-top: 29px;
  display: inline-flex;
  flex-direction: column;
  width: 100%;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    margin-top: 24px;
    width: unset;
    display: flex;
    flex-direction: row;
    justify-content: center;
  }

  @media print, screen and (min-width: ${BREAK_DESKTOP_LAYOUT}px) {
    justify-content: flex-start;
    margin-top: 48px;
  }

  > div {
    margin: 0 auto 16px auto;
    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      margin: 0 20px 16px 0;
    }
    @media print, screen and (min-width: ${BREAK_ROW}px) {
      margin-bottom: 0;
      margin-right: 16px;
    }
    @media print, screen and (min-width: ${Width.XL}px) {
      &:first-child {
        width: 142px;
      }
      > button {
        padding: 0 28px;
      }
    }
    &:last-child {
      margin-bottom: 0;
      @media print, screen and (min-width: ${BREAK_ROW}px) {
        margin-right: 0;
      }
    }
  }
`;

const BelowButtons = styled.div`
  ${flex()};
  flex-direction: row;
  ${topMargin(20, 40, 60)};

  @media print, screen and (min-width: ${BREAK_DESKTOP_LAYOUT}px) {
    justify-content: flex-start;
  }
`;

const Links = styled.div`
  a {
    color: ${Color.WHITE};
  }
  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    margin-top: auto;
  }
  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: unset;
  }
`;

const ImageContainer = styled.div`
  display: none;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    display: block;
    max-width: 134px;
    width: 134px;
    margin-right: 32px;
  }
`;

const ProductList = styled.div`
  text-align: center;
  ${topPadding(80, 64, 24)};
  width: 100%;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    text-align: left;
    > div {
      display: flex;
      flex-direction: column;

      > div {
        display: flex;
        flex-direction: row;
      }
    }
  }
`;

const Disclaimer = styled.div`
  ${p12};
  ${topMargin(20, 16, 16)};
  text-align: center;
  color: ${Color.WHITE};
  opacity: 0.6;

  @media print, screen and (min-width: ${BREAK_DESKTOP_LAYOUT}px) {
    text-align: left;
  }
`;

type Props = {
  text: Node,
  buttons: Node[],
  belowButtons: Node,
  images: AllImages,
  disclaimer: string,
  items: {
    text: string,
    links: string,
    image: {
      name: string,
      alt: string,
    },
  }[],
};

const RightImageListHero = ({ text, buttons, belowButtons, disclaimer, images, items, ...rest }: Props) => {
  const getImage = useImages(images);
  return (
    <Wrapper {...rest}>
      <Text>
        {text}
        <Buttons>{buttons}</Buttons>
        <Disclaimer>{disclaimer}</Disclaimer>
        <BelowButtons>{belowButtons}</BelowButtons>
      </Text>
      <ProductList role="list">
        <div>
          {items.map(({ image: { name, alt }, text, links }, index) => (
            <div key={`OptionalImageBlurbListWithImages-${index}`} role="listitem">
              <ImageContainer className="image">
                <Image image={getImage(name)} alt={alt} />
              </ImageContainer>
              <ProductText>
                {text}
                <Links>{links}</Links>
              </ProductText>
            </div>
          ))}
        </div>
      </ProductList>
    </Wrapper>
  );
};

export default RightImageListHero;
